/**
 * Tools tile: competitor paid-coverage sweep (SALES_INSIGHTS_PLAN.md §5c, M7).
 * Runs weekly on its own (Mon 04:30 Sydney); this is the manual trigger and the
 * health readout. Pages are fetched through Firecrawl (1 credit each).
 */
import { useState } from 'react';
import { Eye, Loader2, Play, Radar, XCircle } from 'lucide-react';
import { runCompetitorSweep, type CompetitorSweepResult } from '../../lib/api/insights.js';

const SITE_LABEL: Record<string, string> = { discovery_alert: 'Discovery Alert', stockwire_x: 'StockWire X' };

export function CompetitorSweepTile() {
  const [maxPages, setMaxPages] = useState('400');
  const [state, setState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [result, setResult] = useState<CompetitorSweepResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (apply: boolean) => {
    const pages = Math.max(1, Math.min(3000, Number(maxPages) || 400));
    if (apply && !window.confirm(`Fetch up to ${pages} new article pages per site through Firecrawl (about ${pages * 2} credits at most) and rebuild the competitor tags?`)) return;
    setState('running'); setError(null); setJobStatus(null); setResult(null);
    try {
      setResult(await runCompetitorSweep({ dry_run: !apply, max_pages: pages }, (s) => setJobStatus(s.status)));
      setState('done');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Request failed'); setState('error');
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Radar className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Competitor Paid-Coverage Sweep</h3>
            <p className="text-sm text-grey-600 mt-1">
              Reads Discovery Alert's and StockWire X's sitemaps and fetches each new ASX article once. A piece counts as
              paid only when it carries their partner disclosure sentence; banner ads are recorded separately as
              advertisers. Results feed the competitor tags on Insights. Runs every Monday 04:30 on its own; a dry run
              fetches at most 25 pages and changes no tags.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <input value={maxPages} onChange={(e) => setMaxPages(e.target.value.replace(/[^\d]/g, ''))}
            aria-label="Pages per site" title="New pages to fetch per site"
            className="w-20 rounded-lg border border-grey-300 px-2 py-2 text-sm" />
          <button onClick={() => run(false)} disabled={state === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run(true)} disabled={state === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
            <Play className="h-4 w-4" /> Run now
          </button>
        </div>
      </div>
      {state !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-2 text-sm">
          {state === 'running' && <div className="flex items-center gap-2 text-primary-600"><Loader2 className="h-4 w-4 animate-spin" /><span>{jobStatus === 'running' ? 'Sweeping…' : 'Queued as a background job…'}</span></div>}
          {state === 'error' && <div className="flex items-center gap-2 text-error-700"><XCircle className="h-4 w-4" /><span>{error}</span></div>}
          {state === 'done' && result && Object.entries(result.sites).map(([site, r]) => (
            <div key={site} className="text-grey-700">
              <strong>{SITE_LABEL[site] ?? site}</strong>
              {r.discover.error ? <span className="text-error-700"> · sitemap error: {r.discover.error}</span> : (
                <> · {r.discover.urls} articles listed, {r.discover.unfetched} not yet fetched</>
              )}
              {' '}· fetched {r.fetch.fetched} ({r.fetch.paid} paid{r.fetch.failed ? `, ${r.fetch.failed} failed` : ''})
              {' '}· {r.pages_fetched_total} fetched to date, {r.paid_total} paid
              {r.import && <> · {r.import.companies} companies{result.dry_run ? '' : `, ${r.import.inserted} tags written`}</>}
              {r.alarm && <span className="text-error-700"> · no paid marker found — check the site</span>}
              {r.unbound_tickers && r.unbound_tickers.length > 0 && (
                <div className="text-xs text-grey-500">Not companies we cover: {r.unbound_tickers.slice(0, 30).join(', ')}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
